import { useCallback, useEffect, useRef, useState } from 'react';
import { useConfig } from '@ohpen/react-utils';
import { useApiClient, UseApiClientOptions, useAuth } from '@ohpen/react-auth';
import { PageSpinner, NotFound } from '@ohpen/enso-ui';

import * as pmpSdk from '@pmp/api';
import { AppLayout, VersionBanner } from '@pmp/components';
import { AuthzProvider, PmpConfigProvider } from '@pmp/contexts';
import { Router } from '../../router';
import { registerAssistantAuth, registerAssistantAgentUrl, PmpAssistantAppConfig } from '../../assistant/agentTransport';
import { AssistantGate } from '../../assistant/AssistantGate';

export const AuthenticatedContent = () => {
  const config = useConfig<PmpAssistantAppConfig>();
  const auth = useAuth();
  const [showBanner, setShowBanner] = useState(false);
  const versionErrorShown = useRef(false);
  const agentUrl = config.APP?.agentUrl;

  // Give the Assistant transport (which lives above AuthProvider) the session token getter
  // and the agent's per-environment URL (config.json APP.agentUrl; see agentTransport.ts).
  useEffect(() => {
    registerAssistantAuth(auth.getAccessToken);
    registerAssistantAgentUrl(agentUrl);
  }, [auth.getAccessToken, agentUrl]);

  const reload = useCallback(() => {
    if ('caches' in globalThis) {
      caches.keys().then(names => Promise.all(names.map(name => caches.delete(name))));
    }

    const url = new URL(globalThis.location.href);
    url.searchParams.set('_', Date.now().toString());
    globalThis.location.href = url.toString();
  }, []);

  const dismissBanner = useCallback(() => setShowBanner(false), []);

  const onVersionError = useCallback(() => {
    if (versionErrorShown.current) return;
    versionErrorShown.current = true;
    setShowBanner(true);
  }, []);

  const apiClientOptions: UseApiClientOptions = {
    config,
    version: config.VERSION,
    onVersionError,
    baseUrl: config?.APP?.apiBaseurl ?? '',
    client: pmpSdk.client,
    sdkModule: pmpSdk,
    getAccessToken: auth.getAccessToken,
  };

  const { isReady: apiClientReady, error: apiClientError } = useApiClient(apiClientOptions);

  if (!apiClientReady) {
    return <PageSpinner />;
  }

  if (apiClientError) {
    return <NotFound />;
  }

  return (
    <PmpConfigProvider getAccessToken={auth.getAccessToken}>
      <AuthzProvider>
        <AssistantGate />
        <AppLayout>
          <Router />
        </AppLayout>
      </AuthzProvider>
      {showBanner && <VersionBanner onReload={reload} onDismiss={dismissBanner} />}
    </PmpConfigProvider>
  );
};
